import {
	WebsiteAnalyticsDailyReport,
	WebsiteAnalyticsLabelCount,
	WebsiteMetricState,
} from '../../../../extViewmodels/sdk/models/Website';

export const RANGE_OPTIONS = [
	{ value: '7', label: 'Last 7 days' },
	{ value: '30', label: 'Last 30 days' },
	{ value: '90', label: 'Last 90 days' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['value'];

export const DEFAULT_RANGE: RangeDays = '30';

/** Rollups land nightly, so a range ends yesterday: today never has a row, and asking for it just shortens the chart. */
export function rangeEndingYesterday(days: number, now: Date = new Date()) {
	const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - (days - 1));
	return { from: toIsoDate(from), to: toIsoDate(to) };
}

function toIsoDate(date: Date) {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

function fromIsoDate(value: string) {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day);
}

const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const wholeNumber = new Intl.NumberFormat('en-US');

export function formatShortDate(value: string) {
	return shortDate.format(fromIsoDate(value));
}

export type MetricTile = { key: string; label: string; hint?: string } & (
	| { kind: 'value'; value: string; caption?: string }
	| { kind: 'notTracked' }
	| { kind: 'unavailable' }
);

interface TileDefinition {
	key: string;
	label: string;
	hint?: string;
	metric: string;
	value: (report: WebsiteAnalyticsDailyReport) => string | null;
}

const TILES: TileDefinition[] = [
	{
		key: 'pageViews',
		label: 'Page views',
		metric: 'pageViews',
		value: report => wholeNumber.format(report.totals.pageViews),
	},
	{
		key: 'visits',
		label: 'Visits',
		metric: 'visits',
		value: report => wholeNumber.format(report.totals.visits),
	},
	{
		key: 'dailyVisitors',
		label: 'Daily visitors',
		hint: 'Each day counts a person once. Someone who visits on 3 different days counts 3 times.',
		metric: 'sumOfDailyUniqueVisitors',
		value: report => wholeNumber.format(report.totals.sumOfDailyUniqueVisitors),
	},
	{
		key: 'bounceRate',
		label: 'Bounce rate',
		metric: 'bounces',
		value: report => (report.totals.bounceRate == null ? null : `${Math.round(report.totals.bounceRate * 100)}%`),
	},
	{
		key: 'leads',
		label: 'Leads',
		metric: 'formSubmissions',
		value: report => wholeNumber.format(report.totals.formSubmissions),
	},
];

function stateOf(report: WebsiteAnalyticsDailyReport, metric: string): WebsiteMetricState | undefined {
	return report.coverage.find(entry => entry.metric === metric)?.state;
}

/** Only `Populated` and `Zero` show a number; a metric nothing measures yet is left out, not shown as a dash. */
export function buildMetricTiles(report: WebsiteAnalyticsDailyReport): MetricTile[] {
	return TILES.flatMap<MetricTile>(({ metric, value, ...tile }) => {
		switch (stateOf(report, metric)) {
			case 'NotImplemented':
			case 'NoRollups':
				return [];
			case 'AwaitingInstrumentation':
				return [{ ...tile, kind: 'notTracked' }];
			case 'InstrumentationUnknown':
				return [{ ...tile, kind: 'unavailable' }];
			case 'Zero': {
				const shown = value(report);
				return [
					shown == null
						? { ...tile, kind: 'unavailable' }
						: { ...tile, kind: 'value', value: shown, caption: 'None in this range' },
				];
			}
			default: {
				const shown = value(report);
				return [shown == null ? { ...tile, kind: 'unavailable' } : { ...tile, kind: 'value', value: shown }];
			}
		}
	});
}

export function hasRollups(report: WebsiteAnalyticsDailyReport) {
	return report.rollups.daysPresent > 0;
}

export function trafficByDay(report: WebsiteAnalyticsDailyReport) {
	return report.days.map(day => ({
		date: day.date,
		label: formatShortDate(day.date),
		pageViews: day.pageViews,
		visits: day.visits,
	}));
}

export function topItems(report: WebsiteAnalyticsDailyReport, dimension: string, limit = 5) {
	const items: WebsiteAnalyticsLabelCount[] =
		report.breakdowns.find(breakdown => breakdown.dimension === dimension)?.items ?? [];
	const top = [...items].sort((a, b) => b.count - a.count).slice(0, limit);
	const max = top[0]?.count ?? 0;
	return top.map(item => ({
		label: item.label,
		count: wholeNumber.format(item.count),
		share: max > 0 ? item.count / max : 0,
	}));
}
